import { alpha, Grid, Typography, useTheme } from '@mui/material';
import { motion } from 'motion/react';
import { FunctionComponent, useEffect, useRef } from 'react';
import { gamePlayStore, useGameplaySelector } from '../../store/GameplayStore';
import { PHASE_DISPLAY_NAMES, Team } from '../../types/GameplayTypes';
import { adaptTransition, AsyncAnimationProps, directed } from '../../util/Animation';
import { devSwitch } from '../../util/Environment';
import { Log } from '../../util/Logging';
import { motionStyleSheet } from '../../util/Styles';
import { Letterbox } from '../layout/ScreenLayout';
import { Buzzer } from './Buzzer';
import { useFaceOffPortal } from './FaceOffPortal';
import { GameBoardZIndex } from './GameBoardZIndex';
import HeadSilhouette from '/assets/images/HeadSilhouette.svg';

const debugLayout = devSwitch(false);

export const FaceOffOverlay: FunctionComponent<AsyncAnimationProps> = (props) => {
	const theme = useTheme();
	const phase = useGameplaySelector(gamePlayStore.selectors.phase);
	const { setPortalContainer } = useFaceOffPortal();
	const portalRef = useRef<HTMLDivElement>(null);

	useEffect(function registerPortalContainer() {
		if (props.show && portalRef.current) {
			setPortalContainer(portalRef.current);
		} else if (!props.show) {
			setPortalContainer(undefined);
		}
	}, [props.show, setPortalContainer]);

	const opacity = directed({ from: 0, to: 1 }, props);

	Log.ui.debug('FaceOffOverlay', phase, props.reverse);

	return props.show && <>
		<Head {...props} team={Team.A} />
		<Head {...props} team={Team.B} />

		<Letterbox {...props} />

		{/* Portal container for question and answers */}
		<Grid ref={portalRef} container spacing={4} style={styles.questionContainer} />

		{/* Title - Hide in other phases to prevent duplication of PhaseTransition during delay */}
		{((phase === 'faceoff') || (phase === 'playOrPass')) && <motion.div
			initial={{ opacity: opacity.from }}
			animate={{ opacity: opacity.to }}
			transition={adaptTransition({ duration: 1, delay: props.reverse ? 0 : 1.2, })}
			onAnimationComplete={props.onComplete}
			style={{
				backgroundColor: theme.palette.game.gold,
				border: `6px solid ${theme.palette.game.white}`,
				...styles.title
			}}>
			<Typography variant="h3" sx={{
				fontWeight: 'bold',
				color: theme.palette.game.textDark,
				textTransform: 'uppercase',
				letterSpacing: '0.1em',
			}}>
				{PHASE_DISPLAY_NAMES[phase]}
			</Typography>
		</motion.div>}

		<Buzzer {...props} team={Team.A} />
		<Buzzer {...props} team={Team.B} />
	</>;
};

type HeadProps = AsyncAnimationProps & {
	team: Team;
};

const Head: FunctionComponent<HeadProps> = (props) => {
	const controllingTeam = useGameplaySelector(gamePlayStore.selectors.controllingTeam);
	const theme = useTheme();

	const isA = props.team === Team.A;
	const xSlide = isA
		? directed({ from: '-100%', to: '-60%' }, props)
		: directed({ from: '100%', to: '60%' }, props);

	function teamFocusStyles() {
		if (props.team === controllingTeam) {
			return { opacity: 1, scale: 1.1, };
		} else if (controllingTeam === undefined) {
			return { opacity: .5, scale: 1, };
		} else {
			return { opacity: .3, scale: .9, };
		}
	}

	return <motion.div
		initial={{ x: xSlide.from }}
		animate={{ x: xSlide.to, ...teamFocusStyles() }}
		transition={adaptTransition({
			duration: 1, delay: props.reverse ? 0 : 1,
			type: 'spring', bounce: .4
		})}
		style={{
			color: theme.palette.game[isA ? 'teamA' : 'teamB'],
			[isA ? 'left' : 'right']: 0,

			position: 'fixed',
			top: '-20%',
			height: '150%',
			zIndex: GameBoardZIndex.Overlay,

			...debugLayout && {
				backgroundColor: alpha('#0000ff', 0.5),
				border: '6px blue',
			},
		}}
	>
		<HeadSilhouette style={{
			height: '100%',
			transform: isA ? '' : 'scaleX(-1)',
			...debugLayout && { border: `2px solid ${theme.palette.game.white}` },
		}} />
	</motion.div>;
};

const styles = motionStyleSheet({
	title: {
		position: 'fixed',
		top: '10%',
		left: '50%',
		transform: 'translateX(-50%) scale(calc((100vh - 20vh - 18vh) / 640px))',
		transformOrigin: 'top center',
		zIndex: GameBoardZIndex.Overlay,
		textAlign: 'center',
		padding: '24px 48px 24px 48px',
		borderRadius: '24px',
		boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
	},
	questionContainer: {
		position: 'fixed',
		top: '20%',
		// bottom: '18%',
		// Bit bigger than expected due to empty grid columns at each side
		left: '10%',
		right: '10%',
		zIndex: GameBoardZIndex.Overlay,
		// height: '1080px',
		transform: 'scale(calc((100vh - 20vh - 18vh) / 640px))',
		transformOrigin: 'top center',
		overflow: 'hidden',
	},
});